import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useState, useRef } from 'react';
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import TiptapEditor from '@/components/Editor/TiptapEditor';
import { sanitizeCommentJson } from '@/lib/sanitizeComment';

export default function EditableComment({ activity, task, onTaskChange, onCancel, onSaved }) {
    const editorRef = useRef(null);
    const [comment, setComment] = useState({
        text: activity.comment?.json ? '' : (activity.comment?.content || ''),
        json: activity.comment?.json || null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { toast } = useToast();
    const { axiosInstance } = useAuth();

    const handleSave = async () => {
        if (!comment.text.trim()) return;

        try {
            setIsSubmitting(true);
            const sanitizedJson = sanitizeCommentJson(comment.json);
            const response = await axiosInstance.patch(
                `/tasks/${task.organization.slug}/${task.key}/comments/${activity.id}`,
                {
                    content: comment.text,
                    json: sanitizedJson
                }
            );

            if (onTaskChange) {
                onTaskChange(response.data);
            }
            onSaved();
        } catch (error) {
            console.error('Error editing comment:', error);
            toast({
                description: "Failed to edit comment",
                variant: "destructive"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <div className="rounded-md border bg-background">
                <TiptapEditor
                    ref={editorRef}
                    content={activity.comment?.json || null}
                    withMentions
                    withCodeBlocks
                    withBubbleMenu
                    withS3Uploads
                    className="min-h-[80px] py-2 px-3"
                    axiosInstance={axiosInstance}
                    organizationSlug={task?.organization?.slug}
                    taskKey={task?.key}
                    onTaskChange={onTaskChange}
                    onEditorReady={(editor) => {
                        editorRef.current = editor;
                    }}
                    onUpdate={({ editor }) => {
                        setComment({
                            text: editor.getText(),
                            json: editor.getJSON()
                        });
                    }}
                    onKeyDown={(view, event) => {
                        if (event.key === 'Escape') {
                            onCancel();
                            return true;
                        }
                        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                            handleSave();
                            return true;
                        }
                        return false;
                    }}
                />
            </div>
            <div className="flex items-center gap-2 mt-2">
                <Button
                    size="sm"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    className="h-7 text-xs"
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={!comment.text.trim() || isSubmitting}
                    className="h-7 text-xs"
                >
                    {isSubmitting ? (
                        <AiOutlineLoading3Quarters className="h-3 w-3 animate-spin mr-1" />
                    ) : null}
                    Save
                </Button>
            </div>
        </div>
    );
}
